import type { Theme } from '@mui/material/styles';
import type { ExecutionBotIp, ExecutionBotIpState } from '../types/attacks';

/**
 * How to present the status of one bot IP in the Executions view (CAO-242).
 *
 * The bug this module fixes: the panel rendered `bot.status` straight from the
 * payload, and that field carried the status recorded when the bot was last
 * sampled into `Execution.participant_bot_ips` — "running" for every bot the
 * snapshot ever captured. Since the panel only appears once an execution has
 * reached a terminal state, every row read **Running** forever.
 *
 * The backend now derives an authoritative `state` on every read
 * (`_resolve_execution_bot_ip_states`) and demotes the recorded value to
 * `last_known_status`. This module renders that verdict — and, crucially, treats
 * a missing or unrecognised `state` as unknown rather than falling back to the
 * recorded status, so a stale "running" can never reappear as Running.
 */

/** Bot statuses that may be echoed verbatim on an inactive row (they already read as "not running"). */
const INACTIVE_STATUS_WORDS = new Set([
  'stopping',
  'shutting_down',
  'shutting-down',
  'stopped',
  'terminating',
  'terminated',
  'error',
  'failed',
]);

/**
 * Semantic tone for the label. Deliberately not a MUI `Chip` color: the palette's
 * `success.main` / `warning.main` only reach ~3.8:1 / ~3.1:1 on the light-mode card
 * surface, so the component resolves the tone against the theme mode instead.
 */
export type ExecutionBotIpTone = 'success' | 'neutral' | 'warning';

export interface ExecutionBotIpStatusPresentation {
  /** Normalised state; anything the backend did not vouch for becomes `unknown`. */
  state: ExecutionBotIpState;
  /** Always-present text label — the status is never communicated by color alone. */
  label: string;
  /** Semantic tone, resolved to a mode-aware color by the component. */
  tone: ExecutionBotIpTone;
  /** Longer explanation for the tooltip and the accessible name. */
  description: string;
}

const normalizeState = (state: unknown): ExecutionBotIpState => {
  const key = typeof state === 'string' ? state.trim().toLowerCase() : '';
  if (key === 'active' || key === 'inactive' || key === 'unknown') return key;
  return 'unknown';
};

const normalizeStatus = (status: unknown): string =>
  typeof status === 'string' ? status.trim().toLowerCase() : '';

/** `shutting_down` -> `Shutting Down`; used for labels, so raw vocabulary stays recognisable. */
export const humanizeBotStatus = (status: string): string =>
  status
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const executionBotIpStatusPresentation = (
  bot: Pick<ExecutionBotIp, 'status' | 'last_known_status' | 'live' | 'state'>
): ExecutionBotIpStatusPresentation => {
  const state = normalizeState(bot.state);
  const status = normalizeStatus(bot.status);
  const lastKnown = normalizeStatus(bot.last_known_status);

  if (state === 'active') {
    return {
      state,
      // The live row's own status is the most precise thing we can say ("Pending" while a
      // replacement bot boots is not the same claim as "Running").
      label: status ? humanizeBotStatus(status) : 'Active',
      tone: 'success',
      description: status
        ? `Bot is an active participant (status: ${status}).`
        : 'Bot is an active participant.',
    };
  }

  if (state === 'inactive') {
    // Echo the raw status only when it already reads as terminal. A bot row still claiming
    // "running" under a finished execution must not put Running back on the screen.
    const echo = INACTIVE_STATUS_WORDS.has(status) ? humanizeBotStatus(status) : 'Ended';
    return {
      state,
      label: echo,
      tone: 'neutral',
      description:
        bot.live && status
          ? `Bot is no longer active (status: ${status}).`
          : lastKnown
            ? `Bot is no longer part of this execution; last seen ${lastKnown}.`
            : 'Bot is no longer part of this execution.',
    };
  }

  return {
    state,
    label: 'Unknown',
    tone: 'warning',
    description: lastKnown
      ? `Current state is unavailable; last seen ${lastKnown}.`
      : 'Current state is unavailable.',
  };
};

/**
 * Mode-aware color for a tone. Light mode needs the darker ramp step: `success.main`
 * / `warning.main` land at ~3.8:1 / ~3.1:1 on the near-white card surface, short of
 * the 4.5:1 AA floor for this 0.7rem label, while `*.dark` clears it. The chip is
 * left unfilled in both modes for the same reason — a tinted background moves the
 * surface toward the text and pushes amber back under the floor.
 */
export const executionBotIpToneColor = (theme: Theme, tone: ExecutionBotIpTone): string => {
  if (tone === 'neutral') return theme.palette.text.secondary;
  const ramp = tone === 'success' ? theme.palette.success : theme.palette.warning;
  return theme.palette.mode === 'dark' ? ramp.main : ramp.dark;
};
